import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { usePlatformAccessLevel } from '../features/platformPermissions/usePlatformAccessLevel';
import type { QmsRegistryKind, QmsReports } from '../features/qms/qmsApi';
import { fetchQmsReports } from '../features/qms/qmsApi';
import { permissionKeyForKind } from '../features/qms/permissionKeys';
import {
  labelQmsAttributability,
  labelQmsOrigin,
  labelTypificationMode,
} from '../features/qms/qmsLabels';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import { SpanishTooltip } from '../components/charts/SpanishTooltip';

interface Props {
  kind: QmsRegistryKind;
  title: string;
  basePath: string;
}

const COLORS_ORIGIN = ['#f59e0b', '#3b82f6'];

function formatMonth(m: string): string {
  const [y, mo] = m.split('-');
  const months = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
  return `${months[parseInt(mo, 10) - 1]} ${y}`;
}

export function QmsReportsPage({ kind, title, basePath }: Props) {
  const { canEdit, ready } = usePlatformAccessLevel(permissionKeyForKind(kind));
  const [reports, setReports] = useState<QmsReports | null>(null);
  const [error, setError] = useState('');

  const reload = useCallback(async () => {
    try {
      if (!canEdit || !ready) return;
      const data = await fetchQmsReports(kind);
      setReports(data);
      setError('');
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Error al cargar reportes');
    }
  }, [kind, canEdit, ready]);

  useEffect(() => {
    reload();
  }, [reload]);

  const monthlyData = useMemo(
    () => (reports?.byMonth ?? []).map((m) => ({ ...m, label: formatMonth(m.month) })),
    [reports]
  );

  const originPieData = useMemo(() => {
    if (!reports) return [];
    const ext = reports.byMonth.reduce((s, m) => s + m.external, 0);
    const int_ = reports.byMonth.reduce((s, m) => s + m.internal, 0);
    return [
      { name: 'Externa (reclamos)', value: ext },
      { name: 'Interna (desvíos)', value: int_ },
    ].filter((d) => d.value > 0);
  }, [reports]);

  const crossData = useMemo(() => {
    if (!reports) return [];
    return reports.crossOriginAttr.map((c) => ({
      ...c,
      originLabel: labelQmsOrigin(c.origin),
      attrLabel: labelQmsAttributability(c.attributability),
      label: `${labelQmsOrigin(c.origin)} — ${labelQmsAttributability(c.attributability)}`,
    }));
  }, [reports]);

  if (!canEdit || !ready) {
    return (
      <div className="py-16 text-center text-muted-foreground">
        No tiene permisos para ver reportes.
      </div>
    );
  }

  return (
    <div className="space-y-8 py-8 px-4 max-w-6xl mx-auto">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Reportes — {title}</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Análisis detallado de no conformidades: tendencias, origen, áreas y procesos.
          </p>
        </div>
        <Link
          to={basePath}
          className="inline-flex items-center rounded-md border border-border bg-card px-4 py-2 text-sm hover:bg-muted"
        >
          Volver al listado
        </Link>
      </div>

      {error && (
        <div className="p-3 bg-destructive/10 border border-destructive/20 rounded text-destructive text-sm">
          {error}
        </div>
      )}

      {!reports ? (
        <div className="py-16 text-center text-muted-foreground">Cargando reportes…</div>
      ) : (
        <>
          {/* Tendencia mensual */}
          <section className="rounded-lg border border-border bg-card p-5 space-y-4">
            <h2 className="text-base font-semibold">Tendencia mensual</h2>
            <p className="text-xs text-muted-foreground">
              Cantidad de NC por mes de incidente, con apertura de externas vs. internas y abiertas vs. cerradas.
            </p>
            {monthlyData.length === 0 ? (
              <p className="text-sm text-muted-foreground">Sin datos suficientes.</p>
            ) : (
              <ResponsiveContainer width="100%" height={320}>
                <BarChart data={monthlyData} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                  <SpanishTooltip />
                  <Legend />
                  <Bar dataKey="external" name="Externas" fill="#f59e0b" stackId="origin" />
                  <Bar dataKey="internal" name="Internas" fill="#3b82f6" stackId="origin" />
                </BarChart>
              </ResponsiveContainer>
            )}
          </section>

          {/* Reclamos externos vs desvíos internos */}
          <section className="rounded-lg border border-border bg-card p-5 space-y-4">
            <h2 className="text-base font-semibold">Reclamos externos vs. desvíos internos</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-sm font-medium text-muted-foreground mb-3">Proporción por origen</h3>
                {originPieData.length === 0 ? (
                  <p className="text-sm text-muted-foreground">Sin datos.</p>
                ) : (
                  <ResponsiveContainer width="100%" height={260}>
                    <PieChart>
                      <Pie
                        data={originPieData}
                        cx="50%"
                        cy="50%"
                        innerRadius={50}
                        outerRadius={90}
                        dataKey="value"
                        nameKey="name"
                        label={({ name, percent }) => `${name} (${((percent ?? 0) * 100).toFixed(0)}%)`}
                        labelLine={false}
                      >
                        {originPieData.map((_, i) => (
                          <Cell key={i} fill={COLORS_ORIGIN[i % COLORS_ORIGIN.length]} />
                        ))}
                      </Pie>
                      <SpanishTooltip />
                    </PieChart>
                  </ResponsiveContainer>
                )}
              </div>

              <div>
                <h3 className="text-sm font-medium text-muted-foreground mb-3">
                  Cruce origen × atribuibilidad
                </h3>
                {crossData.length === 0 ? (
                  <p className="text-sm text-muted-foreground">Sin datos.</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm border-collapse">
                      <thead>
                        <tr className="border-b border-border">
                          <th className="text-left py-2 pr-4 font-medium text-muted-foreground">Origen</th>
                          <th className="text-left py-2 pr-4 font-medium text-muted-foreground">Atribuibilidad</th>
                          <th className="text-right py-2 font-medium text-muted-foreground">Cantidad</th>
                        </tr>
                      </thead>
                      <tbody>
                        {crossData.map((c, i) => (
                          <tr key={i} className="border-b border-border last:border-0">
                            <td className="py-2 pr-4">{c.originLabel}</td>
                            <td className="py-2 pr-4">{c.attrLabel}</td>
                            <td className="py-2 text-right font-semibold tabular-nums">{c.count}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </section>

          {/* Áreas / procesos con más desviaciones */}
          <section className="rounded-lg border border-border bg-card p-5 space-y-4">
            <h2 className="text-base font-semibold">
              Áreas y procesos con más desviaciones reiteradas
            </h2>
            <p className="text-xs text-muted-foreground">
              Ranking de las áreas o procesos que concentran la mayor cantidad de NC, diferenciando origen externo e interno.
            </p>
            {reports.byTypification.length === 0 ? (
              <p className="text-sm text-muted-foreground">Sin datos de tipificación.</p>
            ) : (
              <>
                <ResponsiveContainer width="100%" height={Math.max(200, reports.byTypification.length * 40)}>
                  <BarChart
                    data={reports.byTypification.slice(0, 15)}
                    layout="vertical"
                    margin={{ top: 5, right: 20, left: 10, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                    <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} />
                    <YAxis
                      dataKey="label"
                      type="category"
                      width={180}
                      tick={{ fontSize: 11 }}
                    />
                    <SpanishTooltip />
                    <Legend />
                    <Bar dataKey="external" name="Externas" fill="#f59e0b" stackId="a" />
                    <Bar dataKey="internal" name="Internas" fill="#3b82f6" stackId="a" />
                  </BarChart>
                </ResponsiveContainer>

                <div className="overflow-x-auto mt-4">
                  <table className="w-full text-sm border-collapse">
                    <thead>
                      <tr className="border-b border-border">
                        <th className="text-left py-2 pr-4 font-medium text-muted-foreground">Área / Proceso</th>
                        <th className="text-left py-2 pr-4 font-medium text-muted-foreground">Tipo</th>
                        <th className="text-right py-2 pr-4 font-medium text-muted-foreground">Externas</th>
                        <th className="text-right py-2 pr-4 font-medium text-muted-foreground">Internas</th>
                        <th className="text-right py-2 font-medium text-muted-foreground">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reports.byTypification.map((t, i) => (
                        <tr key={i} className="border-b border-border last:border-0">
                          <td className="py-2 pr-4">{t.label}</td>
                          <td className="py-2 pr-4 text-muted-foreground">{labelTypificationMode(t.mode)}</td>
                          <td className="py-2 pr-4 text-right tabular-nums">{t.external}</td>
                          <td className="py-2 pr-4 text-right tabular-nums">{t.internal}</td>
                          <td className="py-2 text-right font-semibold tabular-nums">{t.total}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </section>
        </>
      )}
    </div>
  );
}
